import ProductHeading from '@components/Product/Details/_ProductHeading';
import Breadcrumb from '@repo/ui/src/components/Breadcrumb';
import Markdown from '@repo/ui/src/components/Markdown';
import { MediaType } from '@repo/ui/src/types/image';
import { LinkProps } from '@repo/ui/src/types/link';
import { PDPTemplate, isTierOne, isTierTwo } from '@views/ProductDetailPage/_enums';
import Product from '@models/product';
import dynamic from 'next/dynamic';
import React, { useCallback } from 'react';
import { AlternateColorProps } from '@components/Product/Details/_ProductSelection';
import { ModalID } from '@repo/ui/src/modules/UI';
import { useUI } from '@repo/ui/src/modules/UI/hooks/useUI';
import { BreadcrumbParentProps } from '@repo/ui/src/types/breadcrumb';
import { classNames } from '@repo/ui/src/utils/classNames';
import { PurchaseOptionsProps } from '@components/Product/Details/_PurchaseOptions';
import { useProductContext } from '@context/ProductProvider';

const SpinSetWrapper = dynamic(() => import('./_SpinSetWrapper'));
const PDPHeroCarousel = dynamic(() => import('@components/Product/Details/_PDPHeroCarousel'));
const PromotionsAndPricing = dynamic(
  () => import('@components/Product/Details/_PromotionsAndPricing'),
);

const TierProductSelection = dynamic(
  () => import('@components/Product/Details/_TierProductSelection'),
);
const ProductSelection = dynamic(() => import('@components/Product/Details/_ProductSelection'));

export interface ProductPageProps {
  template: PDPTemplate;
  breadcrumb: BreadcrumbParentProps;
  spinSetUrl?: string;
  dark?: boolean;
  lifestyleImages?: Array<MediaType>;
  lifestyleVideos?: Array<MediaType>;
  alternateSizes?: {
    sizes: Array<LinkProps>;
  };
  alternateColorsV2?: AlternateColorProps;
  promoMarkdown?: string;
  prop65?: {
    title: string;
    warningBody: string;
  };
  ownerManual?: {
    ownerManualLink: Array<LinkProps>;
  };
  sizes: Array<Product>;
  compatibleWith?: {
    image: MediaType;
    link: LinkProps;
  };
  purchaseOptions?: PurchaseOptionsProps;
}

const ProductPage = ({
  breadcrumb,
  spinSetUrl,
  template,
  lifestyleImages = [],
  prop65,
  ownerManual,
  promoMarkdown,
  sizes,
  alternateColorsV2,
  dark,
  compatibleWith,
  purchaseOptions,
}: ProductPageProps) => {
  const { mainProduct: product } = useProductContext();
  const { pushModal } = useUI();
  const PDPV2 = template === PDPTemplate.PDPV2;
  const NonGrillDark = template === PDPTemplate.NonGrillDark;
  const PDPV2Light = template === PDPTemplate.PDPV2Light;
  const whiteImageBg =
    (template === PDPTemplate.TierOne || template === PDPTemplate.TierTwo) && !dark;
  const darkMode = PDPV2 || NonGrillDark || dark;
  const encodedSpinSetUrl = spinSetUrl ? encodeURI(spinSetUrl) : null;

  const openGalleryDialog = useCallback(
    (images: any, padding: any) => {
      pushModal(ModalID.gallery, {
        images,
        useImagePadding: padding,
      });
    },
    [pushModal],
  );

  const isNewTemplate =
    isTierOne(template) || isTierTwo(template) || template === PDPTemplate.TierThree;

  if (!product) {
    return null;
  }

  const showPrice =
    product.purchasing &&
    !product.purchasing.hideCTA &&
    ![PDPTemplate.PDPV2, PDPTemplate.PDPV2Light, PDPTemplate.TierOne].includes(template);

  return (
    <div
      className={classNames(
        'sm:relative items-start',
        darkMode && template !== PDPTemplate.TierOne && 'bg-neutral-700',
        darkMode && template === PDPTemplate.TierOne && 'bg-neutral-800',
        !darkMode && 'bg-white',
      )}
    >
      {breadcrumb && (
        <Breadcrumb {...breadcrumb} dark={darkMode} title={product.title} className="z-50" />
      )}
      <div className="grid gap-y-6 md:grid-cols-[1fr_calc(554px+8vw)] items-start auto-cols-fr">
        <div
          className={classNames(
            'md:sticky top-[45px] grid gap-y-6 md:grid-flow-col',
            !encodedSpinSetUrl && 'md:pt-[120px] md:pb-16',
            whiteImageBg && 'bg-white',
            PDPV2Light && 'bg-warm-100',
          )}
        >
          <div
            className={classNames(
              'pb-8 md:pb-0 md:pt-8 md:hidden grid gap-y-2 pt-5',
              isNewTemplate && !dark && 'bg-warm-100',
            )}
          >
            <ProductHeading
              className={classNames('md:relative', PDPV2Light && 'bg-warm-100')}
              dark={darkMode}
              hideDescription
              hideSKU={template === PDPTemplate.TierThree}
            />
            {showPrice && (
              <PromotionsAndPricing
                dark={darkMode}
                className="flex flex-wrap px-6 justify-center"
              />
            )}
          </div>
          <div
            className={classNames('z-10 w-full flex flex-col gap-10', PDPV2Light && 'bg-warm-100')}
          >
            <div className="flex flex-col h-full w-full z-0 gap-y-6 items-center justify-center">
              <div
                className={classNames(
                  'relative w-full z-10 flex items-center justify-stretch bg-center bg-contain bg-no-repeat',
                  darkMode &&
                    (template === PDPTemplate.PDPV2 || isTierOne(template)) &&
                    'bg-corrugatedMetal',
                  encodedSpinSetUrl && 'md:h-[calc(100vh_-_76px)]',
                )}
                data-testid="image-wrapper"
              >
                {encodedSpinSetUrl ? (
                  <SpinSetWrapper encodedSpinSetUrl={encodedSpinSetUrl} dark={darkMode} />
                ) : (
                  <PDPHeroCarousel
                    images={[
                      ...(product.images ?? []),
                      ...(product.transparentImages ?? []),
                      ...(lifestyleImages ?? []),
                    ]}
                    openGalleryDialog={openGalleryDialog}
                    dark={darkMode}
                  />
                )}
              </div>
              {product.shortDescription && (
                <div
                  className={classNames(
                    'text-left md:hidden px-6 text-inherit',
                    'md:bg-transparent',
                  )}
                >
                  <Markdown bodySize="large" className={classNames(darkMode && 'text-white')}>
                    {product.shortDescription}
                  </Markdown>
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={classNames(
            'lg:pr-16 md:pl-[3vw] md:min-h-full',
            darkMode && 'bg-neutral-800',
            PDPV2Light && 'bg-warm-200',
            !darkMode && !PDPV2Light && 'bg-warm-100',
          )}
        >
          <div className="px-6 md:px-0 md:mr-4 md:pb-15 md:sticky top-0 self-start py-6 md:py-20">
            {isNewTemplate ? (
              <TierProductSelection
                template={template}
                dark={darkMode}
                prop65={prop65}
                promoMarkdown={promoMarkdown}
                alternateColors={alternateColorsV2}
                ownerManual={ownerManual}
                alternateSize={sizes?.[0]}
                compatibleWith={compatibleWith}
                purchaseOptions={purchaseOptions}
              />
            ) : (
              <ProductSelection
                dark={darkMode}
                prop65={prop65}
                promoMarkdown={promoMarkdown}
                alternateColors={alternateColorsV2}
                ownerManual={ownerManual}
                alternateSize={sizes?.[0]}
                template={template}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
